import { useCallback, useEffect, useState } from "react";

import {
  agendaRTO,
  cambiarEstadoTurnoRTO,
  crearTurnoRTO,
  plantasRTO,
} from "../services/porteria";
import { traerDocumentacion } from "../services/documentacion";
import { hoyAr } from "../shared/fechaAr";
import Patente from "../components/funcionales/Patente";

/* La agenda de turnos de RTO.
 *
 * QUÉ RESUELVE. `rto` dice qué vence y cuándo, o sea el problema. No dice nada
 * de lo que se está haciendo al respecto: quién ya tiene turno, para cuándo y
 * en qué planta. Sin eso la lista de pendientes se mira el lunes, se llama a
 * dos plantas, y el lunes siguiente la lista está igual — no porque no se haya
 * hecho nada, sino porque lo hecho no se anota en ningún lado. Marcos lo
 * llevaba en la cabeza y en un cuaderno.
 *
 * NO ES LA TABLA DEL CENT, Y ÉSA ES LA REGLA QUE SOSTIENE TODO. `rto` la
 * scrapea un job todas las noches y es lo único que decide si un camión puede
 * salir; esto es lo que planificó una persona. Un turno es una INTENCIÓN y una
 * revisión es un HECHO: metidos en la misma tabla, "le sacamos turno" pasaría
 * a leerse como "ya tiene el RTO", y el portón dejaría salir un camión vencido.
 *
 * «EL CENT YA LO REGISTRÓ» SE MUESTRA Y NO SE ESCRIBE. Cerrar el turno solo al
 * ver una revisión posterior es exactamente lo que hizo la geocerca de destino
 * en FlotaBot: cerró cinco tramos que no eran, con la fecha de hoy, y convirtió
 * un dato que faltaba —visible, corregible— en uno inventado que parecía
 * bueno. Un camión puede tener dos turnos abiertos y una revisión no dice a
 * cuál corresponde. Se señala y el click lo da una persona.
 *
 * LA PLANTA ES TEXTO LIBRE. Nadie da de alta una planta: se escribe y aparece
 * en las sugerencias de la próxima, ordenada por uso y con la cuenta al lado.
 * Un desplegable obligaría a abrir un ABM antes de poder anotar el turno que
 * te acaban de dar por teléfono, y ahí no se anota. Misma decisión que los
 * mecánicos de la ticketera.
 *
 * «NO FUE» NO SE BORRA. Que un camión no haya ido a su turno es parte de la
 * operación y es justo lo que hoy no se puede contestar —cuántos turnos se
 * pierden—. Borrarlo deja el dato sin reponer. Mismo criterio que anular un
 * movimiento y que cancelar un ticket.
 */

const ESTADOS = {
  pedido: { texto: "Pedido", tono: "ojo" },
  confirmado: { texto: "Confirmado", tono: "info" },
  hecho: { texto: "Hecho", tono: "ok" },
  no_fue: { texto: "No fue", tono: "gris" },
};

/* Los tres tipos de turno (#111, pedido de Aldo). Los dos nuevos son de un
 * CHOFER, no de una unidad: la fila lleva nombre en vez de patente.
 *
 * «Registro + psicofísico» es UN turno y no dos, y no es un capricho: en
 * Documentación el psicofísico ya quedó como sub-ítem del registro (P-16) —
 * partido en dos acá, el mismo trámite aparecería como dos gestiones y una
 * quedaría siempre «pendiente».
 *
 * «Peligrosas» es el curso de cargas peligrosas del chofer, el mismo
 * `peligrosas_vence` que ya vive en la planilla de Documentación. La agenda
 * no inventa una segunda definición: el vencimiento contra el que se lee el
 * turno sale de ahí (lo resuelve `rto_agenda` en la base). */
const TIPOS = {
  rto: { label: "RTO", deChofer: false },
  registro: { label: "Registro + psicofísico", deChofer: true },
  peligrosas: { label: "Peligrosas", deChofer: true },
};

const deChofer = (tipo) => Boolean(TIPOS[tipo]?.deChofer);

const ABIERTOS = ["pedido", "confirmado"];

const hoy = () => hoyAr(); // el «hoy» argentino, no el de UTC (fechaAr.js)

const fecha = (f) => {
  if (!f) return "—";
  const [a, m, d] = String(f).slice(0, 10).split("-");
  return `${d}/${m}/${a}`;
};

/* Los días entre el turno y el vencimiento. Es el número que dice si el turno
 * sirve: uno para dentro de un mes sobre un RTO que vence la semana que viene
 * llega tarde, y eso no se ve mirando las dos fechas por separado. */
const diasHasta = (desde, hasta) => {
  if (!desde || !hasta) return null;
  const a = new Date(desde + "T00:00:00");
  const b = new Date(hasta + "T00:00:00");
  return Math.round((b - a) / 86400000);
};

// ------------------------------------------------------------------- alta
const Alta = ({ plantas, choferes, onGuardado, prefill }) => {
  const [tipo, setTipo] = useState("rto");
  const [dominio, setDominio] = useState(prefill?.dominio || "");
  const [interno, setInterno] = useState(prefill?.interno || "");
  const [chofer, setChofer] = useState("");
  const [f, setF] = useState(hoy());
  const [hora, setHora] = useState("");
  const [planta, setPlanta] = useState("");
  const [obs, setObs] = useState("");
  const [yendo, setYendo] = useState(false);
  const [error, setError] = useState(null);

  const esDeChofer = deChofer(tipo);

  const guardar = async (e) => {
    e.preventDefault();
    const dom = dominio.toUpperCase().replace(/[^A-Z0-9]/g, "");
    const nombre = chofer.trim();
    if (esDeChofer ? !nombre || !f : !dom || !f) return;
    /* El DNI sólo si el nombre salió de la lista: es lo que engancha el
     * vencimiento de la planilla. Un nombre tipeado a mano guarda el turno
     * igual — sin DNI no hay vencimiento al lado, pero el turno existe, que
     * es lo que importa cuando lo acaban de dar por teléfono. */
    const dni = esDeChofer
      ? choferes.find((c) => c.nombre === nombre)?.dni || null
      : null;
    setYendo(true);
    setError(null);
    try {
      // El service escribe por `createRow`, que es el cuello donde se sella
      // quién cargó. La pantalla no sabe cómo se llama la tabla.
      await crearTurnoRTO({
        tipo,
        dominio: esDeChofer ? null : dom,
        interno: esDeChofer ? null : interno.trim() || null,
        chofer_nombre: esDeChofer ? nombre : null,
        chofer_dni: dni,
        fecha: f,
        hora: hora || null,
        planta: planta.trim() || null,
        obs: obs.trim() || null,
        estado: "pedido",
      });
      /* SE LIMPIA LO DE ESE TURNO Y NADA MÁS, a propósito. Tipo, día y planta
       * quedan puestos: los turnos se anotan de a varios —los que dieron para
       * el mismo día en la misma planta— y volver a elegirlos en cada uno es
       * lo que hace que el tercero quede con el día de hoy. */
      setDominio("");
      setInterno("");
      setChofer("");
      setHora("");
      setObs("");
      onGuardado();
    } catch (err) {
      setError(err.message || "No se pudo guardar");
    } finally {
      setYendo(false);
    }
  };

  return (
    <form className="agenda-alta" onSubmit={guardar}>
      {/* El tipo primero: decide qué se pide abajo. */}
      <div className="campo campo--ancho agenda-tipos">
        <label>Turno de</label>
        <div className="tk-filtros">
          {Object.entries(TIPOS).map(([clave, t]) => (
            <button
              key={clave}
              type="button"
              className={"tk-chip" + (tipo === clave ? " activo" : "")}
              onClick={() => setTipo(clave)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {esDeChofer ? (
        <div className="campo">
          <label htmlFor="ag-chofer">Chofer</label>
          <input
            id="ag-chofer"
            value={chofer}
            onChange={(e) => setChofer(e.target.value)}
            list="agenda-choferes"
            placeholder="Como figura en Documentación"
            autoComplete="off"
            required
          />
          {/* Elegido de la lista, el turno queda atado al DNI y el
              vencimiento de la planilla aparece al lado. Tipeado suelto,
              el turno vale igual. */}
          <datalist id="agenda-choferes">
            {choferes.map((c) => (
              <option key={c.dni} value={c.nombre} />
            ))}
          </datalist>
        </div>
      ) : (
        <>
          <div className="campo">
            <label htmlFor="ag-dom">Patente</label>
            <input
              id="ag-dom"
              value={dominio}
              onChange={(e) => setDominio(e.target.value)}
              placeholder="AB123CD"
              autoComplete="off"
              required
            />
          </div>
          <div className="campo campo--chico">
            <label htmlFor="ag-int">Interno</label>
            <input
              id="ag-int"
              value={interno}
              onChange={(e) => setInterno(e.target.value)}
              autoComplete="off"
            />
          </div>
        </>
      )}
      <div className="campo campo--chico">
        <label htmlFor="ag-fecha">Día</label>
        <input
          id="ag-fecha"
          type="date"
          value={f}
          onChange={(e) => setF(e.target.value)}
          required
        />
      </div>
      {/* La hora NO es obligatoria: muchas veces la planta da el día y nada
          más. Pedirla invita a inventar una, y una hora inventada se lee
          después como un turno perdido por llegar tarde. */}
      <div className="campo campo--chico">
        <label htmlFor="ag-hora">Hora (si la dieron)</label>
        <input
          id="ag-hora"
          type="time"
          value={hora}
          onChange={(e) => setHora(e.target.value)}
        />
      </div>
      <div className="campo">
        {/* Para un chofer no hay «planta»: el turno del registro es en el
            municipio y el de peligrosas donde dé el curso. Es el mismo campo
            —texto libre que se vuelve sugerencia— con la palabra correcta. */}
        <label htmlFor="ag-planta">{esDeChofer ? "Dónde" : "Planta"}</label>
        <input
          id="ag-planta"
          value={planta}
          onChange={(e) => setPlanta(e.target.value)}
          list="agenda-plantas"
          placeholder="Escribila; queda para la próxima"
          autoComplete="off"
        />
        <datalist id="agenda-plantas">
          {plantas.map((p) => (
            <option key={p.planta} value={p.planta}>
              {p.n} {p.n === 1 ? "turno" : "turnos"}
            </option>
          ))}
        </datalist>
      </div>
      <div className="campo campo--ancho">
        <label htmlFor="ag-obs">Obs.</label>
        <input
          id="ag-obs"
          value={obs}
          onChange={(e) => setObs(e.target.value)}
          autoComplete="off"
        />
      </div>
      <button className="tk-boton" disabled={yendo}>
        {yendo ? "Guardando…" : "Anotar turno"}
      </button>
      {error && <p className="agenda-error">{error}</p>}
    </form>
  );
};

// --------------------------------------------------------------- pantalla
/* `prefill` viene de la ficha: interno y patente ya escritos, para que sólo
 * quede poner lo que se va a PROGRAMAR —fecha, hora y planta—. Lo pidió Nico.
 *
 * `Alta` ya aceptaba la prop desde el primer día y NO SE LA PASABA NADIE: ni
 * siquiera esta pantalla. Estaba escrita con la intención y nunca cableada,
 * como `.tabla-wrap`, que existía en el JSX y no en ningún CSS. Un parámetro
 * que no llega no falla: el formulario abre vacío y parece que así se diseñó.
 *
 * NO CAMBIA EL COMPORTAMIENTO DE ENTRAR A LA AGENDA POR EL MENÚ: sin ficha de
 * dónde venir, `prefill` es `undefined` y el alta abre en blanco como siempre. */
const AgendaRTO = ({ prefill }) => {
  const [filas, setFilas] = useState(null);
  const [plantas, setPlantas] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [error, setError] = useState(null);
  const [verCerrados, setVerCerrados] = useState(false);

  const traer = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([agendaRTO(), plantasRTO()]);
      setFilas(a);
      setPlantas(p);
    } catch (e) {
      setError(e.message || "No se pudo cargar la agenda");
    }
  }, []);

  /* Los choferes para las sugerencias del alta. Aparte de `traer` y sin
   * bloquear nada: si la planilla tarda o falla, la agenda abre igual y el
   * nombre se puede tipear a mano — sólo se pierde la sugerencia. */
  useEffect(() => {
    Promise.resolve()
      .then(traerDocumentacion)
      .then((docs) =>
        setChoferes(
          (docs || [])
            .filter((d) => d.nombre && d.activo !== false)
            .map((d) => ({ dni: d.dni, nombre: d.nombre })),
        ),
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
    traer();
  }, [traer]);

  const marcar = async (fila, estado) => {
    try {
      await cambiarEstadoTurnoRTO(fila.id, estado);
      traer();
    } catch (e) {
      setError(e.message || "No se pudo actualizar");
    }
  };

  if (error && !filas) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }
  if (!filas) return <p className="tk-vacio">Cargando…</p>;

  const abiertos = filas.filter((f) => ABIERTOS.includes(f.estado));
  const cerrados = filas.filter((f) => !ABIERTOS.includes(f.estado));
  const listos = abiertos.filter((f) => f.revisado_despues);
  const lista = verCerrados ? cerrados : abiertos;

  return (
    <div className="agenda">
      <div className="conteos">
        <div className="conteo">
          <b>{abiertos.length}</b>
          <span>turnos abiertos</span>
        </div>
        <div className={"conteo" + (listos.length ? " ok" : "")}>
          <b>{listos.length}</b>
          <span>
            el CENT ya registró la revisión
            <br />
            falta marcarlos hechos
          </span>
        </div>
        <div className="conteo">
          <b>{plantas.length}</b>
          <span>plantas usadas</span>
        </div>
      </div>

      <Alta
        plantas={plantas}
        choferes={choferes}
        onGuardado={traer}
        prefill={prefill}
      />

      <div className="tk-filtros">
        <button
          className={"tk-chip" + (!verCerrados ? " activo" : "")}
          onClick={() => setVerCerrados(false)}
        >
          Abiertos ({abiertos.length})
        </button>
        <button
          className={"tk-chip" + (verCerrados ? " activo" : "")}
          onClick={() => setVerCerrados(true)}
        >
          Cerrados ({cerrados.length})
        </button>
      </div>

      <div className="tabla-wrap">
        <table className="tabla">
          <thead>
            <tr>
              {/* «De» y no «Unidad»: desde el #111 un turno puede ser de un
                  chofer (registro + psicofísico, peligrosas). */}
              <th>De</th>
              <th>Turno</th>
              <th>Planta</th>
              <th>Vence</th>
              <th>Estado</th>
              <th>Cargó</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lista.map((f) => {
              const est = ESTADOS[f.estado] || ESTADOS.pedido;
              const margen = diasHasta(f.fecha, f.vence);
              // El turno DESPUÉS del vencimiento no es un detalle: significa
              // que el camión va a estar parado o va a salir vencido. Se marca
              // la fila, no sólo la celda.
              const tarde = margen !== null && margen < 0;
              return (
                <tr key={f.id} className={tarde ? "mal" : ""}>
                  <td className="dato">
                    {deChofer(f.tipo) ? (
                      <>
                        {f.chofer_nombre}
                        {/* Qué trámite es, porque acá conviven tres. Para el
                            RTO no se repite: patente sin más ya se lee como
                            RTO, que es lo que esta agenda fue siempre. */}
                        <span className="sub">{TIPOS[f.tipo]?.label}</span>
                      </>
                    ) : (
                      <>
                        {/* La chapa Mercosur, como en el resto del hub (Igna,
                            03/10: «quedan re facheras»). */}
                        <Patente dominio={f.dominio} />
                        {f.interno ? (
                          <span className="sub">int. {f.interno}</span>
                        ) : null}
                      </>
                    )}
                  </td>
                  <td className="dato">
                    {fecha(f.fecha)}
                    <span className="sub">
                      {f.hora ? String(f.hora).slice(0, 5) : "sin hora"}
                    </span>
                  </td>
                  <td>{f.planta || <span className="tenue">sin anotar</span>}</td>
                  <td className="dato">
                    {f.vence ? fecha(f.vence) : <span className="tenue">—</span>}
                    {margen !== null && (
                      <span className="sub">
                        {tarde
                          ? `${Math.abs(margen)} d tarde`
                          : `${margen} d de margen`}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={"pill " + est.tono}>{est.texto}</span>
                    {f.revisado_despues && ABIERTOS.includes(f.estado) && (
                      /* No se cierra solo: se avisa. Un camión puede tener dos
                         turnos abiertos y la revisión no dice a cuál
                         corresponde — elegir por él es inventar. */
                      <span className="sub">
                        el CENT registró una revisión el{" "}
                        {fecha(f.revisado_despues)}
                      </span>
                    )}
                  </td>
                  <td className="tenue">{f.usuario_nombre || "—"}</td>
                  <td className="agenda-acc">
                    {ABIERTOS.includes(f.estado) && (
                      <>
                        {f.estado === "pedido" && (
                          <button
                            className="tk-boton tk-boton--chico"
                            onClick={() => marcar(f, "confirmado")}
                          >
                            Confirmado
                          </button>
                        )}
                        <button
                          className="tk-boton tk-boton--chico"
                          onClick={() => marcar(f, "hecho")}
                        >
                          Hecho
                        </button>
                        <button
                          className="tk-boton tk-boton--chico tk-boton--flojo"
                          onClick={() => marcar(f, "no_fue")}
                        >
                          No fue
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {lista.length === 0 && (
              <tr>
                <td colSpan="7" className="vacio">
                  {verCerrados
                    ? "Todavía no se cerró ningún turno."
                    : "No hay turnos anotados. Los que se saquen van acá, y así la lista de pendientes deja de repetirse igual todas las semanas."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AgendaRTO;
